import { FormInputWithContextProps } from '@ui/forms/Input/FormInput'
import { InputDateProps } from '@ui/forms/Input/types'
import React, { useCallback, useMemo } from 'react'
import {
  Control,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { tlIfExists } from '@ui/utils/i18n'
import InputDate from './InputDate'
import { dateToString } from './utils'

const isDate = (value: any): value is Date => {
  return value instanceof Date
}

export type BaseFormInputDateProps<T extends FieldValues = FieldValues> = {
  name: Path<T>
  control: Control<T>
} & Omit<InputDateProps, 'onChange' | 'value'>

export type FormInputDateWithContextProps<T extends FieldValues = FieldValues> =
  {
    next?: Path<T>
  } & Omit<BaseFormInputDateProps<T>, 'control'> &
    Omit<InputDateProps, 'onChange' | 'value'>

const BaseFormInputDate = <T extends FieldValues = FieldValues>({
  name,
  control,
  ...props
}: BaseFormInputDateProps<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  })

  const { i18n } = useTranslation('validation')

  const value = useMemo(() => {
    if (field.value == null) return undefined
    if (typeof field.value === 'string') return field.value

    if (isDate(field.value)) {
      return dateToString(field.value)
    }
    return undefined
  }, [field.value])

  const { ref, onBlur, onChange } = field

  const handleChange = useCallback(
    (newValue: string | Date) => {
      onChange(newValue)
      // Also trigger onBlur to ensure form validation runs
      onBlur()
    },
    [onChange, onBlur]
  )

  return (
    <InputDate
      {...props}
      value={value}
      ref={ref}
      onBlur={onBlur}
      errorMessage={
        error?.message
          ? tlIfExists(i18n, `validation:${error.message}`, error.message)
          : props.errorMessage
      }
      onChange={handleChange}
    />
  )
}

const FormInputDateWithContext = <T extends FieldValues = FieldValues>({
  next,
  ...props
}: FormInputWithContextProps<T>) => {
  const { setFocus, control } = useFormContext<T>()

  const onSubmitEditing = useCallback(() => {
    if (next) {
      setFocus(next)
    }
  }, [next, setFocus])

  const enterKeyHint = next
    ? 'next'
    : props.onSubmitEditing
    ? 'done'
    : undefined

  return (
    <BaseFormInputDate
      onSubmitEditing={next ? onSubmitEditing : undefined}
      enterKeyHint={enterKeyHint}
      {...props}
      control={control}
    />
  )
}

export type FormInputDateProps<T extends FieldValues> = {
  control?: Control<T>
} & FormInputDateWithContextProps<T>

const FormInputDate = <T extends FieldValues>(props: FormInputDateProps<T>) => {
  const { control } = props
  if (!control || props.next) {
    return <FormInputDateWithContext {...props} />
  }

  return <BaseFormInputDate {...props} control={control} />
}

export default FormInputDate
